/* eslint-disable import/no-restricted-paths */
import styled from '@emotion/styled/macro';
import {
  Button,
  ScheduleInstallModal,
  Snackbar,
  tokens,
} from '@sunrun/experience-ui-components';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { getEnvironment, getSfdcEnv } from '@sunrun/sales-experience-shared';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { PipelineData, ProspectsByStage } from 'providers/Pipeline';
import { PipelineControls } from 'components/organisms/PipelineHeader';
import {
  PIPELINE_COLUMN_WIDTH,
  PipelineColumn,
} from 'components/organisms/PipelineColumn';
import { useUserProfile } from 'providers/UserProfile';
import { CustomerProfileDrawer } from 'components/organisms/CustomerProfileDrawer';
import { ActionNeededColumn } from 'components/organisms/ActionNeededColumn';
import { useUnreadChats } from 'providers/Chat';
import { useInstallSchedule } from 'hooks/useInstallSchedule';
import { useHybridToken } from 'providers/Auth';
import {
  CHECKOUT,
  CITY_INSPECTION,
  CITY_PERMITTING,
  DESIGN_APPROVAL,
  HOME_REVIEW,
  INSTALL_PREP,
  INSTALLATION,
  NEW_CUSTOMER,
  OFFER_CREATED,
  OPPTY_STAGE_COLUMNS,
  POWER_ON,
  ARC,
  UNKNOWN,
  PRE_SALE_STAGES,
  POST_SALE_STAGES,
  SalePhase,
} from 'components/atoms/PipelineConsts';
import { PipelineErrorDisplay } from 'components/atoms/PipelineErrorDisplay';
import { PullToRefresh } from 'components/atoms/PullToRefresh';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { useOpptyStageColumnsEnabled } from 'hooks/useOpptyStageColumnsEnabled';
import {
  PipelineColumnStage,
  ProspectsByColumn,
  regroupByOpportunityStage,
} from 'helpers/opportunityStageColumns';

const PipelineUrgentItems = memo(
  ({
    pipelineData,
    viewUrgentItems,
  }: {
    pipelineData: ProspectsByStage | undefined;
    viewUrgentItems: () => void;
  }) => {
    const [urgentItems, setUrgentItems] = useState(0);
    useEffect(() => {
      if (!pipelineData) {
        return;
      }
      const urgentItems = Object.entries(pipelineData)
        .flatMap(([stage, prospects]) => prospects)
        .filter((e) => e.urgentAction);
      if (urgentItems.length > 0) {
        setUrgentItems(urgentItems.length);
      }
    }, [pipelineData]);

    return (
      <Snackbar
        open={urgentItems > 0}
        onClose={() => setUrgentItems(0)}
        message={`You have ${urgentItems} new urgent task${
          urgentItems > 1 ? 's' : ''
        } to solve.`}
        autoHideDuration={0}
        type="error"
        colorScheme="dark"
        action={
          <SnackbarButton
            size="sm"
            onClick={() => {
              setUrgentItems(0);
              viewUrgentItems();
            }}>
            Check tasks
          </SnackbarButton>
        }></Snackbar>
    );
  },
);

type PipelineTabProps = {
  controls: PipelineControls;
  setControls: React.Dispatch<React.SetStateAction<PipelineControls>>;
  pipelineData: PipelineData | undefined;
  isPipelineLoading: boolean;
  visible?: boolean;
  pipelineError: Error | undefined;
  setCurrentView: (view: 'pipeline' | 'list') => void;
  onRefresh: () => void | Promise<unknown>;
  isRefreshing?: boolean;
  salePhase?: SalePhase;
};

const PipelineTab = ({
  controls,
  setControls,
  pipelineData,
  isPipelineLoading,
  visible = true,
  pipelineError,
  setCurrentView,
  onRefresh,
  isRefreshing = false,
  salePhase = 'all',
}: PipelineTabProps) => {
  // Customer profile slide-out state
  const [isCustomerProfileOpen, setIsCustomerProfileOpen] = useState(false);
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(
    null,
  );
  const [openPendingTask, setOpenPendingTask] = useState(false);
  const [markClosedLost, setMarkClosedLost] = useState(false);
  const [isDrawerRefreshing, setIsDrawerRefreshing] = useState(false);

  const pipelineV2LayoutEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.PIPELINE_V2_LAYOUT,
  );
  const opptyStageColumnsEnabled = useOpptyStageColumnsEnabled();

  const env = getEnvironment();
  const { hybridToken } = useHybridToken();

  const {
    showScheduleInstallModal,
    currentPipelineProspect,
    isLoadingSchedulerLink,
    schedulerURLError,
    fullSelfSchedulerUrl,
    serviceAppointmentId,
    showInstallScheduleModalAndGetUrl,
    closeModal,
  } = useInstallSchedule({ hybridToken });

  const { userProfile, splatUserType } = useUserProfile();

  const { unReadChatsByContactId } = useUnreadChats();

  const allowExpand = useMediaQuery({
    minWidth: `${PIPELINE_COLUMN_WIDTH * 2}px`,
  });

  const hasSetInitialRep = useRef(false);
  const {
    splatBaseUrl,
    oneBaseUrl,
    salesforceBaseUrl,
    sfPartnerBaseUrl,
    sfdcEnv,
  } = useMemo(() => {
    return {
      splatBaseUrl:
        env === 'production'
          ? 'https://go.sunrun.com'
          : 'https://majstg-go.sunrun.com',
      oneBaseUrl:
        env === 'production'
          ? 'https://sunrunone.com'
          : 'https://staging.sunrunone.com',
      salesforceBaseUrl:
        env === 'production'
          ? 'https://sunrun.my.salesforce.com'
          : 'https://sunrun--uat.sandbox.my.salesforce.com',
      sfPartnerBaseUrl:
        env === 'production'
          ? 'https://sunrun.my.site.com'
          : 'https://sunrun--uat.sandbox.my.site.com',
      sfdcEnv: (userProfile && getSfdcEnv(userProfile)) || 'SFDC',
    };
  }, [env, userProfile]);

  const displayedData: ProspectsByColumn = useMemo(() => {
    if (!pipelineData) {
      return {};
    }
    const prospectsByColumn: ProspectsByColumn = opptyStageColumnsEnabled
      ? regroupByOpportunityStage(pipelineData.prospects)
      : pipelineData.prospects;
    const filteredSortedData = Object.fromEntries(
      Object.entries(prospectsByColumn).map(([stage, prospects = []]) => {
        let filteredData;
        if (
          !controls ||
          (controls.searchTerm.length === 0 &&
            controls.salesReps.length === 0 &&
            !controls.includeSalesRepAction &&
            !controls.includeUrgent &&
            !controls.includedStarred &&
            !controls.includeUnreadMessages)
        ) {
          filteredData = prospects;
        } else {
          filteredData = prospects.filter((pipelineProspect) => {
            if (
              controls.salesReps.length > 0 &&
              pipelineProspect.salesRepId &&
              !controls.salesReps.includes(pipelineProspect.salesRepId)
            ) {
              return false;
            }
            if (
              controls.includeSalesRepAction &&
              !pipelineProspect.salesRepAction
            ) {
              return false;
            }
            if (controls.includeUrgent && !pipelineProspect.urgentAction) {
              return false;
            }
            if (
              controls.includedStarred &&
              !pipelineProspect.starredInPipeline
            ) {
              return false;
            }
            if (
              controls.includeUnreadMessages &&
              (!pipelineProspect.contactId ||
                !unReadChatsByContactId[pipelineProspect.contactId] ||
                unReadChatsByContactId[pipelineProspect.contactId] < 1)
            ) {
              return false;
            }
            const searchTerm = controls.searchTerm;
            if (searchTerm.length === 0) {
              return true;
            }
            const name =
              `${pipelineProspect.firstName} ${pipelineProspect.lastName}`.trim();
            return (
              (pipelineProspect.address &&
                pipelineProspect.address.toLowerCase().includes(searchTerm)) ||
              (pipelineProspect.email &&
                pipelineProspect.email.toLowerCase().includes(searchTerm)) ||
              name.toLowerCase().includes(searchTerm) ||
              (pipelineProspect.jobCode &&
                pipelineProspect.jobCode.toLowerCase().includes(searchTerm)) ||
              (pipelineProspect.phone &&
                pipelineProspect.phone.toLowerCase().includes(searchTerm)) ||
              (pipelineProspect.prospectId &&
                pipelineProspect.prospectId.toLowerCase().includes(searchTerm))
            );
          });
        }
        const sortedData = filteredData.sort((a, b) => {
          if (!controls || controls.sort === 'default') {
            if (a.salesRepAction && !b.salesRepAction) {
              return -1;
            }
            if (b.salesRepAction && !a.salesRepAction) {
              return 1;
            }
            if (a.starredInPipeline && !b.starredInPipeline) {
              return -1;
            }
            if (b.starredInPipeline && !a.starredInPipeline) {
              return 1;
            }
            return Date.parse(b.statusDate) - Date.parse(a.statusDate);
          }
          if (controls.sort === 'newest') {
            return Date.parse(b.statusDate) - Date.parse(a.statusDate);
          }
          if (controls.sort === 'oldest') {
            return Date.parse(a.statusDate) - Date.parse(b.statusDate);
          }
          return 0;
        });
        return [stage as PipelineColumnStage, sortedData];
      }),
    );
    return filteredSortedData;
  }, [
    pipelineData,
    controls,
    unReadChatsByContactId,
    opptyStageColumnsEnabled,
  ]);

  const viewUrgentItems = useCallback(() => {
    setControls(
      (current: PipelineControls) =>
        ({
          ...current,
          searchTerm: '',
          sort: 'default',
          includeSalesRepAction: false,
          includeUrgent: true,
          includedStarred: false,
          includeUnreadMessages: false,
        } as PipelineControls),
    );
  }, [setControls]);

  const openCustomerProfile = useCallback(
    (
      prospectId: string,
      options?: { openPendingTask?: boolean; markClosedLost?: boolean },
    ) => {
      setSelectedProspectId(prospectId);
      setOpenPendingTask(Boolean(options?.openPendingTask));
      setMarkClosedLost(Boolean(options?.markClosedLost));
      setIsCustomerProfileOpen(true);
    },
    [],
  );

  const closeCustomerProfile = useCallback(
    (options?: { refreshPipeline?: boolean }) => {
      setIsCustomerProfileOpen(false);
      setSelectedProspectId(null);
      setOpenPendingTask(false);
      setMarkClosedLost(false);
      if (options?.refreshPipeline) {
        setIsDrawerRefreshing(true);
        Promise.resolve(onRefresh())
          .catch(() => {})
          .finally(() => setIsDrawerRefreshing(false));
      }
    },
    [onRefresh],
  );

  useEffect(() => {
    if (hasSetInitialRep.current || !userProfile || !splatUserType) {
      return;
    }

    if (splatUserType === 'CX Pro') {
      return;
    }

    setControls(
      (current: PipelineControls) =>
        ({
          ...current,
          salesReps: [userProfile.contactId],
        } as PipelineControls),
    );
    hasSetInitialRep.current = true;
  }, [userProfile, splatUserType, controls, setControls]);

  const orderedStages: PipelineColumnStage[] = useMemo(() => {
    if (!userProfile) {
      return [];
    }
    const preSaleOpportunityStages: PipelineColumnStage[] =
      opptyStageColumnsEnabled
        ? [...OPPTY_STAGE_COLUMNS]
        : [NEW_CUSTOMER, OFFER_CREATED];
    if (splatUserType === 'CX Pro') {
      return [
        CHECKOUT,
        HOME_REVIEW,
        DESIGN_APPROVAL,
        CITY_PERMITTING,
        INSTALL_PREP,
        INSTALLATION,
        CITY_INSPECTION,
        POWER_ON,
        ARC,
        UNKNOWN,
      ];
    }
    if (userProfile.isPartner) {
      return [
        ...preSaleOpportunityStages,
        CHECKOUT,
        INSTALLATION,
        CITY_INSPECTION,
        POWER_ON,
        UNKNOWN,
      ];
    }
    return [
      ...preSaleOpportunityStages,
      CHECKOUT,
      HOME_REVIEW,
      DESIGN_APPROVAL,
      CITY_PERMITTING,
      INSTALL_PREP,
      INSTALLATION,
      CITY_INSPECTION,
      POWER_ON,
      ARC,
      UNKNOWN,
    ];
  }, [userProfile, splatUserType, opptyStageColumnsEnabled]);

  const activeSalePhase: SalePhase = pipelineV2LayoutEnabled
    ? salePhase
    : 'all';

  const visibleStages = useMemo(() => {
    if (activeSalePhase === 'pre-sale') {
      return orderedStages.filter((stage) => PRE_SALE_STAGES.includes(stage));
    }
    if (activeSalePhase === 'post-sale') {
      return orderedStages.filter((stage) => POST_SALE_STAGES.includes(stage));
    }
    return orderedStages;
  }, [orderedStages, activeSalePhase]);

  if (pipelineError) {
    return (
      <div style={{ display: visible ? 'block' : 'none' }}>
        <PipelineErrorDisplay
          isListViewRoute={false}
          setCurrentView={setCurrentView}
          currentView="pipeline"
        />
      </div>
    );
  }

  return (
    <PipelinePageContent visible={visible}>
      {showScheduleInstallModal && currentPipelineProspect && (
        <ScheduleInstallModal
          pipelineProspect={currentPipelineProspect}
          oneBaseUrl={oneBaseUrl}
          sfPartnerBaseUrl={sfPartnerBaseUrl}
          salesforceBaseUrl={salesforceBaseUrl}
          sfdcEnv={sfdcEnv}
          onClose={closeModal}
          schedulerUrl={fullSelfSchedulerUrl}
          isLoadingSchedulerUrl={isLoadingSchedulerLink}
          schedulerURLError={schedulerURLError}
          serviceAppointmentId={serviceAppointmentId}
        />
      )}
      {isCustomerProfileOpen && selectedProspectId && (
        <CustomerProfileDrawer
          isOpen={isCustomerProfileOpen}
          onClose={closeCustomerProfile}
          prospectId={selectedProspectId}
          openPendingTask={openPendingTask}
          markClosedLost={markClosedLost}
        />
      )}
      <PullToRefresh onRefresh={onRefresh} isRefreshing={isRefreshing}>
        <ColumnContainer>
          {pipelineV2LayoutEnabled && activeSalePhase === 'all' && (
            <ActionNeededColumn allowExpand={allowExpand} />
          )}
          {visibleStages.map((stage) => (
            <PipelineColumn
              key={stage}
              splatBaseUrl={splatBaseUrl}
              oneBaseUrl={oneBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              stage={stage}
              data={isDrawerRefreshing ? [] : displayedData[stage] ?? []}
              allowExpand={allowExpand}
              unReadChatsByContactId={unReadChatsByContactId}
              isPipelineLoading={isPipelineLoading || isDrawerRefreshing}
              includeSalesRep={
                !controls.salesReps ||
                controls.salesReps.length === 0 ||
                controls.salesReps.find((e) => e !== userProfile?.contactId) !==
                  undefined
              }
              showInstallScheduleModalAndGetUrl={
                showInstallScheduleModalAndGetUrl
              }
              openCustomerProfile={openCustomerProfile}
              showCommission={controls.showCommission}
            />
          ))}
          {!pipelineV2LayoutEnabled && (
            <PipelineUrgentItems
              pipelineData={pipelineData?.prospects}
              viewUrgentItems={viewUrgentItems}
            />
          )}
        </ColumnContainer>
      </PullToRefresh>
    </PipelinePageContent>
  );
};

const PipelinePageContent = styled.div<{ visible: boolean }>`
  position: relative;
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ColumnContainer = styled.div`
  height: 100%;
  margin: 0 1.5rem 1.5rem 1.5rem;
  flex: 1;
  min-height: 0;
  flex-direction: row;
  gap: 0.5rem;
  scroll-snap-type: x mandatory;
  display: flex;
`;

const SnackbarButton = styled(Button)`
  color: black;
  background-color: ${tokens.ERROR_90};
`;

export { PipelineTab };
